/**
 * PasskeysSection — minimum viable:
 * status line ("Passkey enabled" / "No passkey registered") + register button.
 * No list, no delete, no nicknames.
 *
 * Registering a passkey is a sensitive mutation, so the server gates it behind a
 * recent login (403 `requires_recent_login`). When that fires we reveal a reauth
 * step (password field for password users, Google popup otherwise), refresh the
 * token, and retry — mirroring the account-delete / email-change flows. Without
 * this, every returning user with a persisted session (whose `auth_time` is
 * stale) would just see "Passkey setup failed" with no way forward.
 */

import { useState } from 'react';
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'firebase/auth';
import { KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { registerPasskey, REAUTH_REQUIRED } from '@/lib/passkey-service';
import { useAuthStore } from '@/lib/stores/authStore';
import { auth } from '@/lib/firebase';
import { toast } from '@/lib/toast';

export function PasskeysSection() {
  const { user, updateProfile } = useAuthStore();
  const [loading, setLoading] = useState(false);
  // Set when the server rejects registration with `requires_recent_login`: we
  // then reveal a reauth step and retry with a fresh token.
  const [reauthNeeded, setReauthNeeded] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');

  if (!user) return null;

  // Password users reauthenticate by password; OAuth (Google) users via popup.
  const isPasswordUser =
    auth.currentUser?.providerData.some((p) => p.providerId === 'password') ?? false;
  const canSubmit = !loading && (!reauthNeeded || !isPasswordUser || reauthPassword.length > 0);

  const handleRegister = async () => {
    setLoading(true);
    try {
      // If a prior attempt was rejected for a stale session, reauthenticate
      // first so the retried registration carries a fresh `auth_time`. The
      // forced token refresh is required because getIdToken() otherwise returns
      // the cached (stale-auth_time) token the server would reject again.
      if (reauthNeeded) {
        const currentUser = auth.currentUser;
        if (!currentUser) {
          toast.error('Please sign in again to add a passkey.');
          return;
        }
        try {
          if (isPasswordUser) {
            if (!reauthPassword) {
              toast.error('Enter your current password to continue.');
              return;
            }
            await reauthenticateWithCredential(
              currentUser,
              EmailAuthProvider.credential(user.email ?? '', reauthPassword)
            );
          } else {
            await reauthenticateWithPopup(currentUser, new GoogleAuthProvider());
          }
          await currentUser.getIdToken(true);
        } catch {
          toast.error(
            isPasswordUser
              ? 'Incorrect password. Please try again.'
              : 'Google reauthentication failed. Please try again.'
          );
          return;
        }
      }

      const success = await registerPasskey();
      if (success) {
        updateProfile({ passkeyRegistered: true });
        setReauthNeeded(false);
        setReauthPassword('');
        toast.success('Passkey registered.');
      } else {
        toast.info('Passkey setup was cancelled.');
      }
    } catch (err) {
      if (err instanceof Error && err.message === REAUTH_REQUIRED) {
        setReauthNeeded(true);
        toast.error('For your security, confirm your identity to add a passkey.');
        return;
      }
      console.error('Passkey setup failed:', err);
      toast.error('Passkey setup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-8">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Passkeys</h1>
        <p className="text-sm text-muted-foreground">
          Sign in faster with Touch ID, Face ID, or your device biometrics.
        </p>
      </header>

      <div className="space-y-4 rounded-md border border-border bg-card p-4">
        <div className="flex items-center justify-between gap-4">
          <div className="text-sm">
            {user.passkeyRegistered ? (
              <span className="font-medium text-green-600 dark:text-green-400">
                Passkey enabled
              </span>
            ) : (
              <span className="text-muted-foreground">No passkey registered</span>
            )}
          </div>
          <Button
            onClick={handleRegister}
            disabled={!canSubmit}
            variant="outline"
            className="gap-2"
          >
            <KeyRound className="h-4 w-4" />
            {loading
              ? 'Setting up…'
              : user.passkeyRegistered
                ? 'Add another passkey'
                : 'Register a passkey'}
          </Button>
        </div>

        {reauthNeeded && isPasswordUser && (
          <div className="space-y-2">
            <Label htmlFor="passkey-reauth-password">Confirm your password</Label>
            <Input
              id="passkey-reauth-password"
              type="password"
              autoComplete="current-password"
              placeholder="Current password"
              value={reauthPassword}
              onChange={(e) => setReauthPassword(e.target.value)}
              disabled={loading}
            />
            <p className="text-xs text-muted-foreground">
              For your security, re-enter your password to add a passkey.
            </p>
          </div>
        )}
        {reauthNeeded && !isPasswordUser && (
          <p className="text-xs text-muted-foreground">
            For your security, you'll confirm with Google when you click the button.
          </p>
        )}
      </div>
    </div>
  );
}
